import { useCallback } from "react";
import axios from "axios";
import { useSession } from "@clerk/clerk-react";
import { useSupabaseStore } from "@/store/zustand/supabaseStore";
import { transformClaudeCodeApiResponse } from "@/utils/claudeCodeMessageTransformer";

export const useClaudeCodeSessions = (canvasId: string) => {
  const { session } = useSession();

  // Fetch Claude Code conversation history
  const fetchClaudeCodeHistory = useCallback(
    async (sessionId: string) => {
      if (!session || !canvasId) {
        throw new Error("No session or canvas ID available");
      }

      try {
        // Get authentication token
        const token = await session.getToken({ template: "tempo-claude-code" });

        if (!token) {
          throw new Error("Failed to get Claude Code authentication token");
        }

        const claudeCodeApiUrl = `${
          import.meta.env.VITE_BACKEND_BASE_URL
        }/canvases/${canvasId}/claude-code`;

        const response = await axios.get(
          `${claudeCodeApiUrl}/chat/${sessionId}/history?authHeader=${encodeURIComponent(
            `Bearer ${token}`,
          )}`,
        );

        if (response.status === 200) {
          return response.data; // Already formatted as Message[] by the server
        } else {
          console.error(
            "Failed to fetch Claude Code conversation history:",
            response.statusText,
          );
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
      } catch (err: any) {
        console.error("Failed to fetch Claude Code conversation history:", err);
        throw err;
      }
    },
    [session, canvasId],
  );

  // Load Claude Code messages - fetch raw data and parse using existing logic
  const loadClaudeCodeMessages = useCallback(
    async (sessionId: string, conversationId: string) => {
      try {
        // Fetch raw data from Claude Code API
        const rawClaudeMessages = await fetchClaudeCodeHistory(sessionId);

        // Transform raw Claude Code messages to standard format
        const parsedMessages = transformClaudeCodeApiResponse(
          rawClaudeMessages,
          conversationId,
          canvasId,
        );

        // Clear any existing messages for this conversation first
        const existingMessages = Object.values(
          useSupabaseStore.getState().aiChatMessages,
        ).filter((msg) => msg.conversation_id === conversationId);

        if (existingMessages.length > 0) {
          useSupabaseStore.getState().onDbSnapshot("aiChatMessages", {
            newItems: [],
            deletedItems: existingMessages,
          });
        }

        // Add parsed messages to store
        if (parsedMessages.length > 0) {
          useSupabaseStore.getState().onDbSnapshot("aiChatMessages", {
            newItems: parsedMessages,
            deletedItems: [],
          });
        }
      } catch (error) {
        console.error("Failed to load and parse Claude Code messages:", error);
        throw error;
      }
    },
    [canvasId, fetchClaudeCodeHistory],
  );

  // Handle Claude Code session selection and load messages (STORE-ONLY)
  const handleClaudeCodeSessionSelect = useCallback(
    async (conversationId: string): Promise<string | null> => {
      // Get conversation from store to find the session ID
      const conversation =
        useSupabaseStore.getState().aiChatConversations[conversationId];

      if (!conversation?.ai_provider_session_id) {
        console.error(
          "No ai_provider_session_id found for conversation:",
          conversationId,
        );
        return null;
      }

      // If this is a placeholder session ID, there are no messages to load yet
      if (conversation.ai_provider_session_id === "claude_code") {
        return conversation.ai_provider_session_id;
      }

      try {
        // Load messages into store (STORE-ONLY)
        await loadClaudeCodeMessages(
          conversation.ai_provider_session_id,
          conversationId,
        );
        return conversation.ai_provider_session_id;
      } catch (error) {
        console.error("Failed to load Claude Code conversation:", error);
        return conversation.ai_provider_session_id; // Return sessionId even if loading fails
      }
    },
    [loadClaudeCodeMessages],
  );

  // Helper to determine if a conversation is Claude Code based on ai_provider_session_id
  const isClaudeCodeSession = useCallback((conversationId: string) => {
    const conversation =
      useSupabaseStore.getState().aiChatConversations[conversationId];
    return !!conversation?.ai_provider_session_id;
  }, []);

  return {
    fetchClaudeCodeHistory,
    handleClaudeCodeSessionSelect,
    loadClaudeCodeMessages,
    isClaudeCodeSession,
  };
};

export default useClaudeCodeSessions;
